import React, { useState, useEffect } from "react";
import FlashCard from "./components/flashcard";
import MultipleChoice from "./components/MultipleChoice";
import TimedReading from "./components/TimedReading";
import ProfileSetup from "./components/ProfileSetup";
import ProgressHistory from "./components/ProgressHistory";
import MathGames from './components/MathGames';
import ListeningStories from "./components/ListeningStories";
import labels from "./data/labels";
import wordlist_en from "./data/wordlist.en.json";
import wordlist_cs from "./data/wordlist.cs.json";
import wordlist_bg from "./data/wordlist.bg.json";
import wordlist_de from "./data/wordlist.de.json";
import categories_de from "./data/categories.de.json";
import categories_cs from "./data/categories.cs.json";
import categories_bg from "./data/categories.bg.json";
import "./App.css";

function App() {
  const supportedLanguages = ["de", "cs", "bg"];
  const categoriesByLang = {
    de: categories_de,
    cs: categories_cs,
    bg: categories_bg
  };
  const [mode, setMode] = useState(null);
  const [preGame, setPreGame] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [profile, setProfile] = useState(null);
  const [language, setLanguage] = useState(() => {
    const storedProfile = JSON.parse(localStorage.getItem("blitzlesenUser") || "null");
    const storedLang = storedProfile?.language;
    return supportedLanguages.includes(storedLang) ? storedLang : "de";
  });
  const [category, setCategory] = useState("");
  const [viewingProgress, setViewingProgress] = useState(false);
  const [multipleChoiceInProgress, setMultipleChoiceInProgress] = useState(false);
  const [showScoreboard, setShowScoreboard] = useState(false);

  useEffect(() => {
    const savedProfile = localStorage.getItem("blitzlesenUser");
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile(parsedProfile);
      if (parsedProfile.language && supportedLanguages.includes(parsedProfile.language)) {
        setLanguage(parsedProfile.language);
      }
    }
  }, []);

  useEffect(() => {
    const allProfiles = JSON.parse(localStorage.getItem("blitzlesenProfiles") || "{}" );
    let changed = false;
    Object.values(allProfiles).forEach(profile => {
      if (!supportedLanguages.includes(profile.language)) {
        profile.language = "de";
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
    }
    const currentUser = localStorage.getItem("blitzlesenUser");
    if (currentUser) {
      const parsed = JSON.parse(currentUser);
      if (!supportedLanguages.includes(parsed.language)) {
        parsed.language = "de";
        localStorage.setItem("blitzlesenUser", JSON.stringify(parsed));
      }
    }
  }, []);

  // When language changes, update the profile's language in localStorage and state
  useEffect(() => {
    if (profile && supportedLanguages.includes(language)) {
      const updatedProfile = { ...profile, language };
      setProfile(updatedProfile);
      localStorage.setItem("blitzlesenUser", JSON.stringify(updatedProfile));
      // Also update in blitzlesenProfiles
      const allProfiles = JSON.parse(localStorage.getItem("blitzlesenProfiles") || "{}" );
      if (allProfiles[updatedProfile.username]) {
        allProfiles[updatedProfile.username].language = language;
        localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
      }
    }
  }, [language]);

  if (!profile) {
    return <ProfileSetup onProfileSet={p => {
      setProfile(p);
      setLanguage(p.language);
      localStorage.setItem("blitzlesenUser", JSON.stringify(p));
    }} />;
  }

  // Helper function to update scores and persist to localStorage
  const updateScore = (mode, score) => {
    const updatedProfile = { ...profile };
    if (!updatedProfile.scores) {
      updatedProfile.scores = {};
    }
    if (!updatedProfile.scores[mode]) {
      updatedProfile.scores[mode] = [];
    }
    updatedProfile.scores[mode].push({
      score,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem("blitzlesenUser", JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
  };

  const handleSwitchProfile = () => {
    localStorage.removeItem("blitzlesenUser");
    setProfile(null);
  };

  if (viewingProgress) {
    return (
      <ProgressHistory
        onBack={() => setViewingProgress(false)}
        labels={labels[language]}
      />
    );
  }

  const flattenWords = (obj) =>
    Object.values(obj).flat().map((word) => ({ translated: word, base: word }));

  const wordlistMap = {
    en: wordlist_en,
    cs: wordlist_cs,
    bg: wordlist_bg,
    de: wordlist_de
  };

  const baseWords = wordlist_de;

  const selectedList = category
    ? wordlistMap[language]?.[category]
    : Array.isArray(wordlistMap[language])
      ? wordlistMap[language]
      : Object.values(wordlistMap[language] || {}).flat();

  // Build word list with category info for all languages
  function buildWordList(lang, category) {
    const wordlist = wordlistMap[lang];
    const baseList = wordlist_de;
    let result = [];
    if (wordlist && typeof wordlist === 'object' && !Array.isArray(wordlist)) {
      // wordlist is an object of categories
      const categories = category ? [category] : Object.keys(wordlist);
      categories.forEach(cat => {
        const translatedArr = wordlist[cat] || [];
        const baseArr = baseList[cat] || [];
        translatedArr.forEach((translated, i) => {
          result.push({
            translated,
            base: baseArr[i] || translated,
            category: cat
          });
        });
      });
    } else if (Array.isArray(wordlist)) {
      // fallback: flat array (shouldn't happen for cs/bg/de)
      wordlist.forEach((translated, i) => {
        result.push({
          translated,
          base: baseList[i] || translated,
          category: null
        });
      });
    }
    return result;
  }

  const wordData = {
    [language]: buildWordList(language, category)
  };

  // Handler for category change with warning if in progress
  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    if (multipleChoiceInProgress) {
      if (window.confirm("You are in the middle of a game. Progress will be lost and a new game will start. Continue?")) {
        setCategory(newCategory);
        setMultipleChoiceInProgress(false); // Will be set to true by MultipleChoice on new round
      } else {
        // Do not change category
        return;
      }
    } else {
      setCategory(newCategory);
    }
  };

  const handleBackToMenu = () => {
    setMode(null);
    setPreGame(false);
    setPendingMode(null);
    setMultipleChoiceInProgress(false);
  };

  const handleChangeCategory = () => {
    setPreGame(true);
  };

  const handleModeSelect = (selectedMode) => {
    setPendingMode(selectedMode);
    setPreGame(true);
  };

  const handleStartGame = () => {
    setMode(pendingMode);
    setPreGame(false);
  };

  // When language changes in-game, update the profile's language in localStorage and state
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    if (profile && supportedLanguages.includes(newLang)) {
      const updatedProfile = { ...profile, language: newLang };
      setProfile(updatedProfile);
      localStorage.setItem("blitzlesenUser", JSON.stringify(updatedProfile));
      // Also update in blitzlesenProfiles
      const allProfiles = JSON.parse(localStorage.getItem("blitzlesenProfiles") || "{}" );
      if (allProfiles[updatedProfile.username]) {
        allProfiles[updatedProfile.username].language = newLang;
        localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
      }
    }
  };

  // Handler to go to scoreboard
  const handleViewResults = () => setViewingProgress(true);

  // Avatar thumbnail to show at the top in all modes
  const avatarThumb = profile?.avatar ? (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
      {typeof profile.avatar === 'string' && profile.avatar.startsWith('data:image/') ? (
        <img
          src={profile.avatar}
          alt="Avatar"
          style={{ height: '48px', width: '48px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 8px #ccc' }}
        />
      ) : (
        <span style={{ fontSize: '48px', display: 'inline-block', borderRadius: '50%', background: '#f5f5f5', width: 48, height: 48, textAlign: 'center', lineHeight: '48px' }}>{profile.avatar}</span>
      )}
    </div>
  ) : null;

  // Main menu for Blitz Games
  if (!mode) {
    return (
      <div className="App">
        {showScoreboard && (
          <div className="scoreboard-modal-overlay">
            <div className="scoreboard-modal-content" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <ProgressHistory onBack={() => setShowScoreboard(false)} labels={labels[language]} />
              <button className="scoreboard-close-btn" onClick={() => setShowScoreboard(false)}>&#10005;</button>
            </div>
          </div>
        )}
        <button
          className="scoreboard-btn"
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000, background: 'none', border: 'none', boxShadow: 'none', fontSize: '2.2rem', color: '#1976d2', padding: 0 }}
          onClick={() => setShowScoreboard(true)}
        >
          🏆
        </button>
        {avatarThumb}
        <h1>{labels[language]?.title || labels["de"].title}</h1>
        <div className="button-stack" style={{ margin: '32px 0' }}>
          <label htmlFor="lang" style={{ fontWeight: 600, marginBottom: 4, textAlign: 'left' }}>{labels[language].selectLanguage || 'Sprache wählen:'}</label>
          <select
            id="lang"
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            style={{ fontSize: '1.15rem', padding: '12px 16px', borderRadius: '8px', marginBottom: 12, width: '100%' }}
          >
            <option value="de">🇩🇪 Deutsch</option>
            <option value="cs">🇨🇿 Čeština</option>
            <option value="bg">🇧🇬 Български</option>
          </select>
          <button className="green-button" onClick={handleSwitchProfile}>{labels[language].switchProfile || "Switch Profile"}</button>
          <button className="green-button" onClick={() => setMode('math')}>{labels[language].math || 'Math'}</button>
          <button className="green-button" onClick={() => setMode('reading')}>{labels[language].title || 'Reading'}</button>
          <button className="green-button" onClick={() => setMode('listening')}>{labels[language].listening || 'Listening'}</button>
        </div>
      </div>
    );
  }

  // Math category
  if (mode === 'math') {
    return (
      <div className="App">
        <button onClick={handleBackToMenu} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px', marginBottom: 24 }}>
          ⬅️ {labels[language].backToGame || "Back to Menu"}
        </button>
        <MathGames language={language} onScore={(subcat, score) => updateScore(subcat, score)} />
        {/* Scoreboard button */}
        <button
          className="small-button"
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}
          onClick={() => setShowScoreboard(true)}
        >
          🏆
        </button>
        {showScoreboard && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 2000 }}>
            <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px #0002', padding: 32, minWidth: 320 }}>
              <button
                style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}
                onClick={() => setShowScoreboard(false)}
              >
                &#10005;
              </button>
              <ProgressHistory onBack={() => setShowScoreboard(false)} labels={labels[language]} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Reading category (show options)
  if (mode === 'reading') {
    return (
      <div className="App">
        <button onClick={handleBackToMenu} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px', marginBottom: 24 }}>
          ⬅️ {labels[language].backToGame || "Back to Menu"}
        </button>
        <h1 className="text-2xl font-bold text-center mb-4">{labels[language].title || 'Reading'}</h1>
        <div className="button-stack" style={{ margin: '32px 0' }}>
          <button className="green-button" onClick={() => setMode('flash')}>{labels[language].flash}</button>
          <button className="green-button" onClick={() => setMode('multiple')}>{labels[language].multiple}</button>
          <button className="green-button" onClick={() => setMode('timed')}>{labels[language].timed}</button>
        </div>
      </div>
    );
  }

  // Listening placeholder
  if (mode === 'listening') {
    return (
      <div className="App">
        <button onClick={handleBackToMenu} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px', marginBottom: 24 }}>
          ⬅️ {labels[language].backToGame || "Back to Menu"}
        </button>
        <ListeningStories language={language} labels={labels[language]} />
        {/* Scoreboard button */}
        <button
          className="small-button"
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}
          onClick={() => setShowScoreboard(true)}
        >
          🏆
        </button>
        {showScoreboard && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 2000 }}>
            <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px #0002', padding: 32, minWidth: 320 }}>
              <button
                style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}
                onClick={() => setShowScoreboard(false)}
              >
                &#10005;
              </button>
              <ProgressHistory onBack={() => setShowScoreboard(false)} labels={labels[language]} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Game in progress: show game area and back/change category buttons
  if (mode) {
    return (
      <div className="App">
        {avatarThumb}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '24px' }}>
          <button onClick={handleBackToMenu} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px' }}>
            ⬅️ {labels[language].backToGame || "Back to Menu"}
          </button>
          <button onClick={handleChangeCategory} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px' }}>
            {labels[language].changeCategory || "Change Category"}
          </button>
        </div>
        {mode === "flash" && (
          <FlashCard
            words={wordData[language]}
            language={language}
            category={category}
            onScore={score => updateScore("flash", score)}
            onViewResults={handleViewResults}
          />
        )}
        {mode === "multiple" && (
          <MultipleChoice
            words={wordData[language]}
            language={language}
            labels={labels[language]}
            category={category}
            onScore={score => updateScore("multiple", score)}
            setInProgress={setMultipleChoiceInProgress}
            onViewResults={handleViewResults}
          />
        )}
        {mode === "timed" && (
          <TimedReading
            words={wordData[language]}
            language={language}
            labels={labels[language]}
            category={category}
            onScore={score => updateScore("timed", score)}
            onViewResults={handleViewResults}
          />
        )}
      </div>
    );
  }

  // Pre-game: show category selection and start button
  if (preGame && pendingMode) {
    return (
      <div className="App">
        {avatarThumb}
        <h2>{labels[language]?.category || "Choose Category"}</h2>
        <select
          id="cat"
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{ fontSize: '1.2rem', padding: '10px', margin: '20px 0', borderRadius: '8px' }}
        >
          <option value="">{labels[language].allCategories}</option>
          {Object.entries(categoriesByLang[language]).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <div className="button-stack" style={{ margin: '20px 0' }}>
          <button className="green-button" onClick={handleStartGame}>
            {labels[language].start || "Start Game"}
          </button>
          <button className="green-button" onClick={handleBackToMenu}>
            ⬅️ {labels[language].backToGame || "Back to Menu"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {avatarThumb}
      <h1>{labels[language]?.title || labels["de"].title}</h1>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="lang">Language: </label>
        <select
          id="lang"
          value={language}
          onChange={(e) => handleLanguageChange(e.target.value)}
        >
          <option value="de">🇩🇪 Deutsch</option>
          <option value="cs">🇨🇿 Čeština</option>
          <option value="bg">🇧🇬 Български</option>
        </select>
      </div>
      {categoriesByLang[language] && (
        <div style={{ marginBottom: "20px" }}>
          <label htmlFor="cat">{labels[language].category} </label>
          <select id="cat" value={category} onChange={handleCategoryChange}>
            <option value="">{labels[language].allCategories}</option>
            {Object.entries(categoriesByLang[language]).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>
      )}
      <button onClick={handleSwitchProfile} className="small-button" style={{ marginBottom: "20px" }}>
        {labels[language].switchProfile || "Switch Profile"}
      </button>
      <button onClick={() => setViewingProgress(true)} className="small-button" style={{ marginBottom: "20px" }}>
        {labels[language].viewProgress || "View Progress"}
      </button>
      <div style={{ marginBottom: "20px" }}>
        <button onClick={() => handleModeSelect("flash")}>{labels[language].flash}</button>
        <button onClick={() => handleModeSelect("multiple")}>{labels[language].multiple}</button>
        <button onClick={() => handleModeSelect("timed")}>{labels[language].timed}</button>
      </div>
    </div>
  );
}

export default App;